/* eslint-disable @atlaskit/platform/no-module-level-eval */

import { fg } from '@atlaskit/platform-feature-flags/fg';

const ConfluencePageFragment = `
  fragment ConfluencePageFragment on ConfluencePage {
    __typename
    title
    pageStatus: status
    subtype
    ancestors {
      __typename
      id
      title
    }
    links {
      __typename
      base
      webUi
    }
    space {
      id
      name
    }
  }
`;

const ConfluenceBlogPostFragment = `
  fragment ConfluenceBlogPostFragment on ConfluenceBlogPost {
    __typename
    title
    blogStatus: status
    links {
      __typename
      base
      webUi
    }
    space {
      id
      name
    }
  }
`;

const ConfluenceWhiteboardFragment = `
  fragment ConfluenceWhiteboardFragment on ConfluenceWhiteboard {
    __typename
    title
    links {
      base
      webUi
    }
    space {
      id
      name
    }
  }
`;

const ConfluenceSlideFragment = `
  fragment ConfluenceSlideFragment on ConfluenceSlide {
    __typename
    title
    links {
      base
      webUi
    }
    space {
      id
      name
    }
  }
`;

const AtlasProjectFragment = `
  fragment AtlasProjectFragment on TownsquareProject {
    __typename
    key
    name
    url
    iconData
  }
`;

const AtlasGoalFragment = `
  fragment AtlasGoalFragment on TownsquareGoal {
    __typename
    key
    name
    url
    iconData
  }
`;

const JiraSingleIssueLineTextFieldFragment = `
  fragment JiraSingleIssueLineTextFieldFragment on JiraSingleLineTextField {
    text
		issue @include(if: $includeJiraIssueMetadata) {
			projectField {
				project {
					name
				}
			}
			issueTypeField {
				issueType {
					name
				}
			}
		}
  }
`;

const JiraIssueFragment = `
  fragment JiraIssueFragment on JiraIssue {
    __typename
    key
    webUrl
    statusCategory {
      name,
      colorName
    }
    issueType {
      avatar {
        small
      }
    }
    fieldsById(ids: ["summary"]) {
      edges {
        node {
          __typename
          ...JiraSingleIssueLineTextFieldFragment
        }
      }
    }
  }
  ${JiraSingleIssueLineTextFieldFragment}
`;

const GraphDocumentFragment = `
  fragment GraphDocumentFragment on ExternalDocument {
    id
    type {
        category
    }
    displayName
    parent @optIn(to: "ExternalDataDepotQueryV2"){
        ... on ExternalDocument {
            displayName
        }
    }
    url
    provider {
        providerId
    }
  }
`;

const GraphRemoteLinkFragment = `
  fragment GraphRemoteLinkFragment on ExternalRemoteLink {
	  id
		displayName
		category @optIn(to: "ExternalDataDepotQueryV2")
		url
    provider {
			providerId
		}
  }
`;

const LoomVideoFragment = `
  fragment LoomVideoFragment on LoomVideo {
    name
    loomUrl: url
    owner @include(if: $includeLoomVideoMetadata) {
      name
    }
    playableDuration @include(if: $includeLoomVideoMetadata)
  }
`;

const ExternalPullRequestFragment = `
  fragment ExternalPullRequestFragment on ExternalPullRequest {
    id
    displayName
    url
    status
    provider {
      providerId
    }
  }
`;

const ExternalWorkItemFragment = `
  fragment ExternalWorkItemFragment on ExternalWorkItem {
    id
    displayName
    url
    workItemStatus: status
    provider {
      providerId
    }
  }
`;

const AssetsObjectFragment = `
  fragment AssetsObjectFragment on AssetsObject {
    id
		displayTypename @optIn(to: "AssetsApFields")
    links {
      web
    }
    assetsLabel: label
    objectKey
    assetsIcon: avatar {
      url48
    }
    objectType {
      schema {
        name
        ${fg('cdm_ga') ? `assetsSchemaDisplayName: displayName` : ''}
      }
    }
  }
`;

const AssetsObjectTypeFragment = `
fragment AssetsObjectTypeFragment on AssetsObjectType {
	id
	displayTypename @optIn(to: "AssetsApFields")
	links {
		web
	}
	assetsLabel: name
	${fg('cdm_ga') ? `assetsDisplayName: displayName` : ''}
	assetsIcon: icon {
		url48
	}
	schema {
		assetsSchemaName: name
		${fg('cdm_ga') ? `assetsSchemaDisplayName: displayName` : ''}
	}
}
`;

const AssetsSchemaFragment = `
fragment AssetsSchemaFragment on AssetsSchema {
	id
	displayTypename @optIn(to: "AssetsApFields")
	links {
		web
	}
	assetsLabel: name
	${fg('cdm_ga') ? `assetsDisplayName: displayName` : ''}
}
`;

export const operationName = 'QuickFindActivitiesQuery';

// TODO: we could use partials instead of fragments, similarly to how it's done in search-page
export const quickFindRecentActivitiesQuery: string = `
  query ${operationName}(
    $filteredProducts: [String!]
    $filteredObjectTypes: [String!]
    $filteredEventTypes: [String!]
    $itemsLimit: Int!
    $filteredRootContainerIds: [ID!]
    $after: String
    $includeJiraIssueMetadata: Boolean = false
    $includeLoomVideoMetadata: Boolean = false
    $includeSearchAppMetadata: Boolean = false
  ) {
    activity {
      myActivity {
        all(
          filter: {
            arguments: {
              products: $filteredProducts
              objectTypes: $filteredObjectTypes
              eventTypes: $filteredEventTypes
            }
            rootContainerIds: $filteredRootContainerIds
          }
          first: $itemsLimit
          after: $after
        ) {
          __typename
          edges {
            cursor
            __typename
            node {
              __typename
              object {
                __typename
                id
                type
                product
                contributors @include(if: $includeSearchAppMetadata) {
                  profile {
                    accountId
                    name
                    picture
                  }
                }
                data {
                  __typename
                  ... on JiraIssue {
                    statusField @include(if: $includeSearchAppMetadata) {
                      status {
                        name
                        statusCategory {
                          mercuryName
                        }
                      }
                    }
                  }
                  ...ConfluencePageFragment
                  ...ConfluenceBlogPostFragment
                  ...ConfluenceWhiteboardFragment
                  ...ConfluenceSlideFragment
                  ...AtlasProjectFragment
                  ...AtlasGoalFragment
                  ...JiraIssueFragment
                  ...GraphDocumentFragment
									...GraphRemoteLinkFragment
									...LoomVideoFragment
									...ExternalPullRequestFragment
									...ExternalWorkItemFragment
                  ${fg('assets_rovo_search_all') ? `...AssetsObjectFragment` : ''}
									${fg('assets_rovo_search_all') ? `...AssetsObjectTypeFragment` : ''}
									${fg('assets_rovo_search_all') ? `...AssetsSchemaFragment` : ''}
                }
              }
              event {
                __typename
                eventType
                timestamp
              }
            }
          }
        }
      }
    }
  }
  ${ConfluencePageFragment}
  ${ConfluenceBlogPostFragment}
  ${ConfluenceWhiteboardFragment}
  ${ConfluenceSlideFragment}
  ${AtlasProjectFragment}
  ${AtlasGoalFragment}
  ${JiraIssueFragment}
  ${GraphDocumentFragment}
	${GraphRemoteLinkFragment}
	${LoomVideoFragment}
	${ExternalPullRequestFragment}
	${ExternalWorkItemFragment}
  ${fg('assets_rovo_search_all') ? AssetsObjectFragment : ''}
	${fg('assets_rovo_search_all') ? AssetsObjectTypeFragment : ''}
	${fg('assets_rovo_search_all') ? AssetsSchemaFragment : ''}
`;
